<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import { useLightgalleryState } from '~/composables/useLightgalleryState'

import Container from '~/components/layout/Container.vue'
import type { ReferenceLandingPageItem } from '~/types/api/ReferenceDetail'
import LightgalleryBase from '~/components/global/LightgalleryBase.vue'

const props = defineProps<{
  landingPageItem: ReferenceLandingPageItem
}>()

const lightgalleryState = useLightgalleryState({
  galleryId: props.landingPageItem.id.toString(),
  allItems: props.landingPageItem.children,
  itemsToShow: 4,
  dynamicItems: computed(() => {
    const items = []

    for (const item of props.landingPageItem.children) {
      if (item.module_data.video_url) {
        items.push({
          src: item.module_data.video_url,
          thumb: youtubeThumbnail(item.module_data.video_url),
        })

        continue
      }

      items.push({
        src: thumbnail(item.image, 'lightbox'),
        thumb: thumbnail(item.image, 'lightbox-thumb'),
      })
    }

    return items
  }),
})

function getImageData(image: string) {
  return getImageSrcSizesData(image, {
    defaultFormat: '1200x2400',
    sizes: `
      (max-width: 575.99px) 95vw,
      (max-width: 1199.99px) 50vw,
      1200px
    `,
  })
}
</script>

<template>
  <div class="c-gallery">
    <Container class="c-gallery__container">
      <h2 class="c-gallery__title">
        {{ landingPageItem.name }}
      </h2>

      <LightgalleryBase
        :lightgallery-state="lightgalleryState"
        :class="[
          'c-gallery__images',
          `has-items-${lightgalleryState.dynamicItems.value.length}`,
        ]"
      >
        <button
          v-for="(item, index) in lightgalleryState.visibleItems.value"
          :key="index"
          :class="[
            'c-gallery__item',
            `c-gallery__item--index-${index + 1}`,
            item.module_data.video_url
              ? 'c-gallery__item--video'
              : 'c-gallery__item--image',
          ]"
          @click="lightgalleryState.openGalleryAtIndex(index)"
        >
          <Icon v-if="item.module_data.video_url" name="play" class="sc-icon" />

          <img
            class="sc-img"
            :src="
              item.module_data.video_url
                ? youtubeThumbnail(item.module_data.video_url, 'sddefault')
                : getImageData(item.image).src
            "
            :sizes="
              item.module_data.video_url
                ? undefined
                : getImageData(item.image).sizes
            "
            :srcset="
              item.module_data.video_url
                ? undefined
                : getImageData(item.image).srcset
            "
            :alt="item.name || ''"
            loading="lazy"
          />
        </button>

        <div
          v-if="lightgalleryState.moreItemsCount.value > 0"
          class="c-gallery__item c-gallery__item--show-more"
        >
          <button
            class="c-link c-link--block-hover c-link--with-leading-bar"
            @click="lightgalleryState.openGalleryAtIndex(0)"
          >
            <strong>{{
              $t('pages.references.detail.parts.gallery.viewWholeGallery')
            }}</strong>
          </button>
        </div>
      </LightgalleryBase>
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-gallery {
  margin-bottom: 120px;

  @include breakpoint-down('lg') {
    margin-bottom: 100px;
  }

  @include breakpoint-down('md') {
    margin-bottom: 60px;
  }

  &__title {
    font-size: 40px;
    line-height: 1;
    margin-bottom: 28px;
  }

  &__images {
    display: grid;
    grid-template:
      'item1 item2 item4' 30fr
      'item1 item3 item4' 12fr
      'item1 item3 show-more' 16fr
      / 36fr 46fr 46fr;
    gap: 20px;
    aspect-ratio: 1320 / 620;

    @include breakpoint-down('xl') {
      aspect-ratio: 1320 / 600;
    }

    @include breakpoint-down('md') {
      gap: 5px;
    }

    @include breakpoint-down('xs') {
      grid-template:
        'item1 item2' 1fr
        'item1 item3' 1fr
        'item4 item4' auto
        'show-more show-more' auto
        / 36fr 46fr;
      aspect-ratio: unset;
    }

    &.has-items-1 {
      grid-template:
        'item1' 1fr
        / 1fr;
    }

    &.has-items-2 {
      grid-template:
        'item1 item2' 1fr
        / 1fr 1fr;
    }

    &.has-items-3 {
      grid-template:
        'item1 item2' 1fr
        'item1 item3' 1fr
        / 1fr 1.6fr;
    }

    &.has-items-4 {
      grid-template:
        'item1 item2 item4' 1fr
        'item1 item3 item4' 1fr
        / 36fr 46fr 46fr;

      @include breakpoint-down('xs') {
        grid-template:
          'item1 item2' 1fr
          'item1 item3' 1fr
          'item4 item4' auto
          / 36fr 46fr;
      }
    }
  }

  &__item {
    position: relative;
    border: none;
    display: grid;
    place-items: center;
    background-color: var(--color-border);
    padding: 0;
    cursor: pointer;
    transition: all var(--transition-duration) var(--transition-timing-function);

    .sc-icon {
      position: relative;
      z-index: 2;
      display: grid;
      place-items: center;
      width: 86px;
      height: 86px;
      border-radius: 86px;
      background-color: var(--color-dark);
      color: var(--color-light);
      font-size: 29px;
      padding-left: 6px;
      transition: all var(--transition-duration)
        var(--transition-timing-function);

      @include breakpoint-down('sm') {
        font-size: 18px;
        width: 54px;
        height: 54px;
      }
    }

    .sc-img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &:hover {
      filter: brightness(0.9) grayscale(0.6);

      .sc-icon {
        opacity: 0.8;
      }
    }

    &--index-1 {
      grid-area: item1;

      @include breakpoint-down('xs') {
        aspect-ratio: 4 / 7;
      }
    }

    &--index-2 {
      grid-area: item2;
    }

    &--index-3 {
      grid-area: item3;
    }

    &--index-4 {
      grid-area: item4;

      @include breakpoint-down('xs') {
        aspect-ratio: 2 / 1;
      }
    }

    &--show-more {
      display: grid;
      place-items: center;
      padding: 30px 20px;
      grid-area: show-more;
      background-color: transparent;
    }
  }
}
</style>
